/**
 * Excel-ээр бөөнөөр бараа нэмэх урсгалын шалгуурууд.
 *
 * Өмнө нь ямар ч шалгалт байгаагүй тул:
 *  - .txt, зураг зэрэг Excel биш файл ч уншигдаж, хэрэглэгчид ямар ч мессеж
 *    гардаггүй байсан (юу болсныг мэдэхгүй үлддэг);
 *  - мөр байхгүй файл импортлоход мөн чимээгүй өнгөрдөг байв.
 */

/** Excel гэж хүлээж авах өргөтгөлүүд */
export const SPREADSHEET_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

/** `<Upload accept>`-д өгөх утга */
export const SPREADSHEET_ACCEPT = SPREADSHEET_EXTENSIONS.join(',');

/**
 * Сонгосон файл Excel мөн эсэх.
 *
 * Зөвхөн өргөтгөлөөр шалгана — хөтөч дээр агуулгыг нь урьдчилж уншихгүйгээр
 * бодит төрлийг мэдэх боломжгүй. Хэрэв өргөтгөл нь зөв атлаа агуулга нь буруу
 * бол `findEmptySheetError` барина.
 *
 * @returns {string|null} алдааны мессеж, зөв бол `null`
 */
export const findSpreadsheetFileError = (file) => {
  const name = file?.name ?? '';
  const extension = name.slice(name.lastIndexOf('.')).toLowerCase();

  if (!name.includes('.') || !SPREADSHEET_EXTENSIONS.includes(extension)) {
    return `Зөвхөн Excel файл (${SPREADSHEET_EXTENSIONS.join(', ')}) оруулна уу.`;
  }

  return null;
};

/** Excel импортод зөвшөөрөх дээд хэмжээ (MB) */
export const MAX_SPREADSHEET_SIZE_MB = 5;

const BYTES_IN_MB = 1024 * 1024;

/**
 * Сонгосон файлын хэмжээ хязгаарт багтаж байгаа эсэх.
 *
 * Өмнө нь ямар ч шалгалт байгаагүй тул 7.44MB файл ч шууд сервер рүү илгээгдэж,
 * буцаж ирсэн хариунд хэмжээний талаар юу ч хэлэгддэггүй байв — хэрэглэгч юуг
 * засахаа мэдэхгүй үлддэг.
 *
 * @returns {string|null} алдааны мессеж, зөв бол `null`
 */
export const findSpreadsheetSizeError = (file) => {
  const size = file?.size;
  if (size == null) return null;

  if (size === 0) return 'Файл хоосон байна.';

  if (size > MAX_SPREADSHEET_SIZE_MB * BYTES_IN_MB) {
    const actualMb = (size / BYTES_IN_MB).toFixed(2);
    return `Файлын хэмжээ ${MAX_SPREADSHEET_SIZE_MB}MB-аас бага байх ёстой (одоо ${actualMb}MB).`;
  }

  return null;
};

/**
 * Уншсан хуудсанд ажиллах мөр байгаа эсэх.
 *
 * @returns {string|null} алдааны мессеж, мөртэй бол `null`
 */
export const findEmptySheetError = (rows) =>
  rows?.length ? null : 'Файлд бараа олдсонгүй. Загвар файлын дагуу бөглөнө үү.';

/**
 * БӨӨНӨӨР БАРАА НЭМЭХ ЗАГВАРЫН ШАЛГУУР
 *
 * Сервер тал Excel-ийн мөрүүдийг шалгалтгүй хүлээж авдаг тул:
 *  - `quantity` дээрх бутархай тоог `parseInt`-ээр ЧИМЭЭГҮЙ тайрдаг (1.5 → 1),
 *    текст/хоосон утга `NaN` болж тоо хэмжээ хоосон орох;
 *  - олдохгүй SKU (толгой/сүүл зайтай, өөр мерчантын, буруу баганын нэр) дээр
 *    HTTP 500 буцааж, `Cannot set properties of undefined (setting 'totalUnit')`
 *    гэсэн дотоод JS алдааны текстийг хэрэглэгчид харуулдаг.
 *
 * Тиймээс мөрүүдийг СЕРВЕР РҮҮ ИЛГЭЭХЭЭС ӨМНӨ энд шалгаж, аль мөр буруу
 * байгааг монгол хэлээр хэлнэ.
 */

/** Барааг олоход хэрэглэгддэг баганууд — аль нэг нь заавал бөглөгдсөн байна */
export const PRODUCT_KEY_COLUMNS = ['sku', 'barcode', 'baseCode', 'shoppyCode'];

/** Тоо хэмжээний багана */
export const QUANTITY_COLUMN = 'quantity';

/** Захиалж болох тоо хэмжээний доод хязгаар */
const MIN_IMPORT_QUANTITY = 1;

/** Мөрийн Excel дээрх дугаар — толгой мөрийг тооцно */
const excelRowNumber = (index) => index + 2;

/** Утгыг текст болгож толгой/сүүлийн зайг тайрна */
const cleanText = (value) => (value == null ? '' : String(value).trim());

/**
 * Мөрийн тоо хэмжээний алдааг олно.
 *
 * @returns {string|null} алдааны мессеж, зөв бол `null`
 */
const findQuantityError = (value) => {
  const text = cleanText(value);

  if (text === '') return 'Тоо хэмжээ хоосон байна.';

  const number = Number(text);

  if (Number.isNaN(number)) return `Тоо хэмжээ тоо байх ёстой («${text}»).`;
  if (!Number.isInteger(number)) return `Тоо хэмжээ бүхэл байх ёстой («${text}»).`;
  if (number < MIN_IMPORT_QUANTITY) {
    return `Тоо хэмжээ ${MIN_IMPORT_QUANTITY}-ээс багагүй байх ёстой («${text}»).`;
  }

  return null;
};

/**
 * Excel-ээс уншсан мөрүүдийг цэвэрлэж, алдаатай мөрүүдийг олно.
 *
 * Текст баганын толгой/сүүлийн зайг тайрна — « QLQ-AUT-001 » гэж бичсэн SKU
 * сервер дээр олдохгүй HTTP 500 үүсгэдэг байсан.
 *
 * @param {Array<object>} rows `sheet_to_json`-ийн гаралт
 * @returns {{rows: Array<object>, errors: string[]}} цэвэрлэсэн мөрүүд ба алдаанууд
 */
export const prepareProductImportRows = (rows = []) => {
  const errors = [];

  // Загварын түлхүүр багана нэг ч мөрөнд байхгүй бол баганын нэр буруу гэсэн үг
  const hasKeyColumn = rows.some((row) =>
    PRODUCT_KEY_COLUMNS.some((column) => cleanText(row?.[column]) !== '')
  );

  if (!hasKeyColumn) {
    return {
      rows: [],
      errors: [
        `Файлд «${PRODUCT_KEY_COLUMNS.join('», «')}» баганаас нэг ч олдсонгүй. Excel загварыг татаж, баганын нэрийг хөндөхгүйгээр бөглөнө үү.`,
      ],
    };
  }

  const preparedRows = rows.map((row, index) => {
    const rowNumber = excelRowNumber(index);
    const prepared = { ...row };

    // Барааг олох кодуудын зайг тайрна
    PRODUCT_KEY_COLUMNS.forEach((column) => {
      if (column in prepared) prepared[column] = cleanText(prepared[column]);
    });

    const hasProductKey = PRODUCT_KEY_COLUMNS.some((column) => prepared[column]);
    if (!hasProductKey) {
      errors.push(`${rowNumber}-р мөр: SKU (эсвэл бар код) хоосон байна.`);
      return prepared;
    }

    const quantityError = findQuantityError(prepared[QUANTITY_COLUMN]);
    if (quantityError) {
      errors.push(`${rowNumber}-р мөр: ${quantityError}`);
      return prepared;
    }

    prepared[QUANTITY_COLUMN] = String(Number(cleanText(prepared[QUANTITY_COLUMN])));
    return prepared;
  });

  return { rows: preparedRows, errors };
};
